import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import {
  Phone, Mail, MapPin, Sparkles, Award, CheckCircle2, Languages, Clock,
  Shield, FileText, Camera, AlertTriangle, ArrowRight, Loader2, Star,
  Car, Calendar, MessageSquare, ChevronRight, Quote, Zap, Smartphone, Globe,
} from "lucide-react";
import { cn } from "@/lib/utils";

// Titel-Präfixe, die wir aus dem Anzeige-Namen entfernen, um die reine
// Vorname/Nachname-Kombination für E-Mail und Web-Slug zu bekommen.
const TITLE_PREFIX = /^(dipl\.?[-‐ ]?ing\.?|dr\.?|prof\.?|mag\.?|ing\.?)\s+/i;

function _normalizeUmlauts(s) {
  return (s || "")
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss");
}

/** Liefert `vorname.nachname` aus einem ggf. mit Titel versehenen Namen. */
function buildEmailLocal(name) {
  let n = (name || "").trim();
  // Mehrere Titel-Präfixe wiederholt entfernen
  while (TITLE_PREFIX.test(n)) n = n.replace(TITLE_PREFIX, "").trim();
  const parts = n.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "kontakt";
  if (parts.length === 1) {
    return _normalizeUmlauts(parts[0]).replace(/[^a-z0-9]/g, "") || "kontakt";
  }
  const first = _normalizeUmlauts(parts[0]).replace(/[^a-z0-9]/g, "");
  const last = _normalizeUmlauts(parts[parts.length - 1]).replace(/[^a-z0-9]/g, "");
  return `${first}.${last}`;
}

/** Liefert einen URL-tauglichen Slug wie `thomas-mueller` aus einem Namen. */
function buildWebSlug(name) {
  let n = (name || "").trim();
  while (TITLE_PREFIX.test(n)) n = n.replace(TITLE_PREFIX, "").trim();
  return _normalizeUmlauts(n)
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "partner";
}

/** Ableiten des Bundeslands aus den ersten 2 PLZ-Stellen (grobes DE-Mapping). */
function bundeslandFromPLZ(plz) {
  if (!plz) return null;
  const n = parseInt(String(plz).slice(0, 2), 10);
  if (Number.isNaN(n)) return null;
  if (n <= 9) return "Sachsen";
  if (n <= 16) return "Berlin & Brandenburg";
  if (n <= 19) return "Mecklenburg-Vorpommern";
  if (n <= 25) return "Hamburg & Schleswig-Holstein";
  if (n <= 31) return "Niedersachsen";
  if (n <= 33) return "NRW";
  if (n <= 36) return "Hessen";
  if (n === 37 || n === 38) return "Niedersachsen";
  if (n === 39) return "Sachsen-Anhalt";
  if (n >= 40 && n <= 48) return "NRW";
  if (n === 49) return "Niedersachsen";
  if (n >= 50 && n <= 53) return "NRW";
  if (n >= 54 && n <= 56) return "Rheinland-Pfalz";
  if (n >= 57 && n <= 59) return "NRW";
  if (n >= 60 && n <= 65) return "Hessen";
  if (n === 66) return "Saarland";
  if (n >= 67 && n <= 69) return "Rheinland-Pfalz";
  if (n >= 70 && n <= 79) return "Baden-Württemberg";
  if (n >= 80 && n <= 87) return "Bayern";
  if (n === 88) return "Baden-Württemberg";
  if (n >= 89 && n <= 97) return "Bayern";
  if (n >= 98 && n <= 99) return "Thüringen";
  return null;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACCIDENT_STEPS = [
  { num: 1, title: "Unfallstelle absichern", text: "Warnblinker an, Warndreieck mind. 100 m hinter dem Fahrzeug aufstellen, Warnweste anziehen.", icon: AlertTriangle },
  { num: 2, title: "Verletzte versorgen & 112", text: "Erste Hilfe leisten und bei Verletzten sofort den Notruf wählen.", icon: Shield },
  { num: 3, title: "Polizei rufen (110)", text: "Bei Personenschäden, größeren Sachschäden oder Streitigkeiten unbedingt die Polizei hinzuziehen.", icon: Shield },
  { num: 4, title: "Beweise sichern", text: "Fotos vom Schaden, Endposition der Fahrzeuge, Bremsspuren und Verkehrszeichen aus mehreren Winkeln.", icon: Camera },
  { num: 5, title: "Daten austauschen", text: "Name, Anschrift, Versicherung, Kennzeichen und Kontaktdaten aller Beteiligten und Zeugen erfassen.", icon: FileText },
  { num: 6, title: "Kfz-Sachverständigen beauftragen", text: "Bei Haftpflichtschäden hat der Geschädigte Anspruch auf einen unabhängigen Gutachter — Kosten zahlt die gegnerische Versicherung.", icon: Award },
  { num: 7, title: "Gutachten & Reparatur", text: "Mit dem Gutachten zur Werkstatt, Restwert und Wiederbeschaffungswert sind dokumentiert — Versicherung reguliert.", icon: CheckCircle2 },
];

// Standard-Dienstleistungen eines Kfz-Sachverständigen. Werden ergänzend zu
// den individuellen `partner.specializations` angezeigt — sodass die Seite
// auch ohne konfigurierte Spezialisierungen einen kompletten Leistungs-Block
// für Unfallgeschädigte zeigt.
const DEFAULT_SERVICES = [
  {
    title: "Haftpflichtgutachten",
    desc: "Vollständige Schadendokumentation nach unverschuldetem Unfall — die gegnerische Versicherung trägt die Kosten.",
    icon: Shield,
  },
  {
    title: "Kaskogutachten",
    desc: "Schadenbewertung für deine eigene Voll- oder Teilkaskoversicherung inkl. Reparaturkosten und Restwert.",
    icon: FileText,
  },
  {
    title: "Kostenvoranschlag",
    desc: "Schnelle Reparaturkostenermittlung bei Bagatell- und Kleinschäden — ideal als Verhandlungsgrundlage.",
    icon: Calendar,
  },
  {
    title: "Wertgutachten",
    desc: "Marktwert- und Wertermittlung für Verkauf, Kauf, Erbschaft, Scheidung oder Finanzamt.",
    icon: Award,
  },
  {
    title: "Oldtimer-Gutachten",
    desc: "Klassiker-Bewertung mit Zustandsnote 1–5 — anerkannt von H-Kennzeichen-Behörden und Versicherern.",
    icon: Star,
  },
  {
    title: "Beweissicherung",
    desc: "Gerichtsfeste Foto- und Schadensdokumentation — entscheidend bei strittigen Schadenherganganalysen.",
    icon: Camera,
  },
  {
    title: "Unfallrekonstruktion",
    desc: "Technische Analyse von Kollisionsgeschehen, Geschwindigkeiten und Plausibilität für Anwälte & Gerichte.",
    icon: Zap,
  },
  {
    title: "Schaden­abwicklung",
    desc: "Begleitung der gesamten Regulierung mit der Versicherung — du musst dich um nichts kümmern.",
    icon: CheckCircle2,
  },
];

const FAQS = [
  {
    q: "Was kostet ein Kfz-Gutachten?",
    a: "Bei einem Haftpflichtschaden, an dem du nicht schuld bist, übernimmt die gegnerische Versicherung die Kosten — auch das Gutachten ist dann für dich kostenlos. Bei Eigenschäden oder Wertgutachten richtet sich der Preis nach dem Schadensumfang oder Fahrzeugwert.",
  },
  {
    q: "Wie schnell bekomme ich mein Gutachten?",
    a: "Mit der Easy-Gutachter-Plattform erstellen wir dein Gutachten innerhalb von 24 Stunden nach der Begutachtung — vollständig digital, mit Foto-Dokumentation und Unterschrift.",
  },
  {
    q: "Habe ich ein Recht auf einen unabhängigen Sachverständigen?",
    a: "Ja. Bei einem unverschuldeten Haftpflichtschaden hast du als Geschädigter laut BGH-Urteilen das Recht auf einen unabhängigen Gutachter deiner Wahl — die gegnerische Versicherung darf dir keinen vorschreiben.",
  },
  {
    q: "Was ist der Unterschied zwischen Wiederbeschaffungswert und Restwert?",
    a: "Der Wiederbeschaffungswert ist der Preis, den du für ein vergleichbares Fahrzeug zahlen müsstest. Der Restwert ist der Verkaufswert deines beschädigten Autos. Die Differenz minus Selbstbeteiligung bekommst du von der Versicherung erstattet.",
  },
  {
    q: "Kann ich mein Auto vor Erstellung des Gutachtens reparieren lassen?",
    a: "Nein — das Fahrzeug muss zunächst von einem Sachverständigen begutachtet werden. Vorher Reparieren reduziert deine Beweislage und kann zu Versicherungs-Kürzungen führen.",
  },
];

export default function PartnerProfile() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    postal_code: "",
    case_type: "haftpflicht",
    message: "",
  });

  useEffect(() => {
    let abort = false;
    setLoading(true);
    axios.get(`${API}/public/partners/${slug}`)
      .then(({ data }) => { if (!abort) setPartner(data.partner); })
      .catch(() => { if (!abort) navigate("/partner"); })
      .finally(() => { if (!abort) setLoading(false); });
    return () => { abort = true; };
  }, [slug, navigate]);

  // SEO head + JSON-LD LocalBusiness
  useEffect(() => {
    if (!partner) return;
    const title = `${partner.name} — Kfz-Gutachter in ${partner.city || "deiner Region"} · Easy Gutachter`;
    const desc = partner.bio_short || `Verifizierter Kfz-Sachverständiger ${partner.name} in ${partner.city || "Deutschland"}. Schnelle, unabhängige Schadenbegutachtung mit der Easy-Gutachter-Plattform.`;
    document.title = title;
    setMeta("description", desc);
    setOg("og:title", title);
    setOg("og:description", desc);
    setOg("og:type", "profile");
    if (partner.avatar_url) setOg("og:image", `${BACKEND_URL}${partner.avatar_url}`);

    // JSON-LD LocalBusiness
    const ld = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": `${window.location.origin}/partner/${partner.slug}`,
      "name": `Easy Gutachter · ${partner.name}`,
      "image": partner.avatar_url ? `${BACKEND_URL}${partner.avatar_url}` : undefined,
      "description": desc,
      "telephone": partner.mobile || partner.phone,
      "email": partner.email,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": partner.city,
        "postalCode": partner.postal_code,
        "addressCountry": partner.country || "DE",
      },
      ...(partner.geo_lat && partner.geo_lng ? {
        "geo": { "@type": "GeoCoordinates", "latitude": partner.geo_lat, "longitude": partner.geo_lng },
      } : {}),
      "areaServed": (partner.service_postal_codes || []).map((plz) => ({ "@type": "PostalCodeArea", "postalCode": plz })),
      "knowsLanguage": partner.languages,
      "knowsAbout": partner.specializations,
    };
    let script = document.getElementById("partner-jsonld");
    if (!script) {
      script = document.createElement("script");
      script.id = "partner-jsonld";
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(ld);
    return () => { script?.remove(); };
  }, [partner]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Bitte Name, E-Mail und Nachricht ausfüllen");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/partners/${slug}/contact`, form);
      toast.success("Anfrage gesendet — wir melden uns schnellstmöglich bei dir.");
      setForm({ name: "", email: "", phone: "", postal_code: "", case_type: "haftpflicht", message: "" });
    } catch (err) {
      toast.error("Anfrage fehlgeschlagen — bitte später erneut versuchen.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
      </div>
    );
  }
  if (!partner) return null;

  const phone = partner.phone;
  const mobile = partner.mobile;
  // Handy-Nummer des Gutachters hat Vorrang — der Anruf-Button wählt IMMER
  // die vom User eingetragene Mobilnummer (Fallback: Festnetz).
  const primaryPhone = mobile || phone; // für Top-Bar- & Hero-CTA
  // Branded E-Mail + Website immer im Format vorname.nachname@easygutachter.de
  // bzw. easygutachter.de/vorname-nachname — unabhängig davon, was im Profil hinterlegt ist.
  const brandedEmail = `${buildEmailLocal(partner.name)}@easygutachter.de`;
  const brandedWebSlug = buildWebSlug(partner.name);
  const brandedWeb = `easygutachter.de/${brandedWebSlug}`;
  // Bundesland aus PLZ ableiten ("Gutachter in ganz NRW")
  const bundesland = bundeslandFromPLZ(partner.postal_code) || partner.city || "Deutschland";

  return (
    <div className="min-h-screen bg-white text-zinc-950" data-testid="partner-profile-page">
      {/* Top nav — wirkt wie die eigene Website des Gutachters */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-zinc-200" data-testid="partner-top-nav">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <a href="#top" className="flex items-center" data-testid="partner-top-logo">
            <img src="/easy-gutachter-logo.png" alt={partner.name} className="h-12 w-auto select-none" />
          </a>
          <nav className="hidden md:flex items-center gap-7 text-sm font-medium">
            <a href="#leistungen" className="text-zinc-600 hover:text-emerald-700 transition" data-testid="nav-leistungen">Leistungen</a>
            <a href="#unfall-leitfaden" className="text-zinc-600 hover:text-emerald-700 transition" data-testid="nav-unfall">Was tun bei einem Unfall?</a>
            <a href="#faq" className="text-zinc-600 hover:text-emerald-700 transition" data-testid="nav-faq">Häufige Fragen</a>
            <a href="#kontakt" className="text-zinc-600 hover:text-emerald-700 transition" data-testid="nav-kontakt">Kontakt</a>
          </nav>
          {primaryPhone && (
            <a href={`tel:${primaryPhone.replace(/\s/g, "")}`} data-testid="partner-cta-call">
              <Button size="sm" className="brand-gradient-bg text-white">
                <Phone className="h-3.5 w-3.5 mr-1.5" /> {primaryPhone}
              </Button>
            </a>
          )}
        </div>
      </header>

      <div id="top" />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 brand-gradient-soft" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-10 pb-14 grid lg:grid-cols-12 gap-10 items-center">
          {/* Photo */}
          <div className="lg:col-span-4 flex justify-center lg:justify-start" data-testid="partner-hero-photo">
            <div className="relative">
              <div className="h-56 w-56 lg:h-64 lg:w-64 rounded-full overflow-hidden border-4 border-white shadow-xl bg-white flex items-center justify-center">
                {partner.avatar_url ? (
                  <img src={`${BACKEND_URL}${partner.avatar_url}`} alt={partner.name} className="w-full h-full object-cover" />
                ) : (
                  <img src="/easy-gutachter-logo.png" alt="Easy Gutachter" className="w-2/3 object-contain" />
                )}
              </div>
              <div className="absolute -bottom-3 -right-3 bg-white rounded-2xl shadow-lg border border-zinc-200 px-3 py-2 flex items-center gap-1.5">
                <Award className="h-4 w-4 text-emerald-600" />
                <span className="text-xs font-semibold text-zinc-900">Verifiziert</span>
              </div>
            </div>
          </div>
          {/* Info */}
          <div className="lg:col-span-8 text-center lg:text-left">
            <Badge className="bg-white/85 text-emerald-700 border border-emerald-200" data-testid="partner-hero-badge">
              <Sparkles className="h-3 w-3 mr-1" /> Easy Gutachter Partner · {partner.city || "Deutschland"}
            </Badge>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mt-4" data-testid="partner-hero-name">
              {partner.name}
            </h1>
            <p className="text-base sm:text-lg text-zinc-600 leading-relaxed mt-4 max-w-3xl">
              {partner.bio_short || `Dein Kfz-Sachverständiger in ${partner.city || "deiner Region"}. Schadensgutachten, Wertgutachten und Unfallrekonstruktion — schnell, transparent und vollständig digital.`}
            </p>

            {/* USP-Block: Vorteile für Unfallgeschädigte — clean auf Hero-Gradient, ohne Card-Hintergrund */}
            <div className="mt-7 max-w-3xl mx-auto lg:mx-0" data-testid="partner-usp-block">
              <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-emerald-700 flex items-center gap-2">
                <span className="h-px w-8 bg-emerald-500/60" />
                Sie hatten einen Unfall?
              </div>
              <div className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950 mt-2 leading-tight">
                Wir helfen Ihnen <span className="text-emerald-700">schnell und zuverlässig</span>.
              </div>
              <ul className="grid sm:grid-cols-2 sm:grid-rows-3 sm:grid-flow-col gap-x-7 gap-y-3 mt-6 text-left" data-testid="partner-usp-list">
                {[
                  "100% kostenfrei für Geschädigte",
                  "Zertifizierter Kfz-Sachverständiger",
                  "Versicherungsunabhängig",
                  `Gutachter in ganz ${bundesland}`,
                  "Termine direkt bei Ihnen vor Ort — keine Anfahrtskosten",
                ].map((u) => (
                  <li key={u} className="flex items-start gap-3 text-[15px] sm:text-base text-zinc-800">
                    <span className="mt-0.5 h-5 w-5 rounded-full bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center shrink-0 shadow-sm">
                      <CheckCircle2 className="h-3.5 w-3.5 text-white" strokeWidth={3} />
                    </span>
                    <span className="font-medium leading-snug">{u}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex flex-wrap justify-center lg:justify-start gap-2 mt-5">
              {(partner.specializations || []).map((s) => (
                <Badge key={s} variant="outline" className="bg-white border-zinc-300 text-zinc-700 font-semibold">{s}</Badge>
              ))}
            </div>
            <div className="flex flex-wrap justify-center lg:justify-start gap-3 mt-7">
              <a href="#kontakt" data-testid="partner-cta-contact">
                <Button size="lg" className="brand-gradient-bg text-white">
                  <MessageSquare className="h-4 w-4 mr-2" /> Anfrage senden
                </Button>
              </a>
              {primaryPhone && (
                <a href={`tel:${primaryPhone.replace(/\s/g, "")}`}>
                  <Button size="lg" variant="outline">
                    <Phone className="h-4 w-4 mr-2" /> {primaryPhone}
                  </Button>
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <section className="border-y border-zinc-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center text-xs sm:text-sm">
          <TrustItem icon={Clock} title="In 24 Stunden" desc="Gutachten lieferbar" />
          <TrustItem icon={Shield} title="DSGVO-konform" desc="Verschlüsselte Ablage" />
          <TrustItem icon={Award} title="Unabhängig & geprüft" desc="Verifizierter Partner" />
          <TrustItem icon={Zap} title="Vollständig digital" desc="Live-Foto, e-Signatur, PDF" />
        </div>
      </section>

      {/* About */}
      {partner.bio_long && (
        <section className="max-w-4xl mx-auto px-6 lg:px-10 py-14" data-testid="partner-about">
          <h2 className="font-display text-3xl font-bold tracking-tight">Über {partner.name.split(" ")[0]}</h2>
          <div className="mt-6 text-base text-zinc-700 leading-relaxed whitespace-pre-line">
            {partner.bio_long}
          </div>
        </section>
      )}

      {/* Services / Specializations — immer sichtbar */}
      <section id="leistungen" className="bg-zinc-50/60 py-14 scroll-mt-20" data-testid="partner-services">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <Badge className="bg-white text-emerald-700 border border-emerald-200">
            <Award className="h-3 w-3 mr-1" /> Leistungen
          </Badge>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-4">
            Das biete ich dir
          </h2>
          <p className="text-zinc-600 mt-3 max-w-2xl">
            Unabhängige, gerichtsfeste Kfz-Gutachten und Schadenbewertungen — schnell, transparent und vollständig digital abgewickelt.
          </p>

          {/* Individuelle Spezialisierungen (sofern hinterlegt) */}
          {(partner.specializations || []).length > 0 && (
            <div className="flex flex-wrap gap-2 mt-6" data-testid="partner-specializations">
              {partner.specializations.map((s) => (
                <Badge key={s} className="bg-white border border-emerald-200 text-emerald-700 font-semibold px-3 py-1.5">
                  <Sparkles className="h-3 w-3 mr-1" /> {s}
                </Badge>
              ))}
            </div>
          )}

          {/* Standard-Leistungen — immer rendern */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
            {DEFAULT_SERVICES.map((s) => {
              const Icon = s.icon;
              return (
                <Card key={s.title} className="p-5 hover:border-emerald-300 hover:shadow-md transition-all" data-testid={`service-${s.title.replace(/\W+/g, "-").toLowerCase()}`}>
                  <div className="h-10 w-10 rounded-lg brand-gradient-bg flex items-center justify-center mb-3">
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <div className="font-display font-bold tracking-tight text-zinc-900">{s.title}</div>
                  <p className="text-sm text-zinc-500 leading-relaxed mt-1.5">{s.desc}</p>
                </Card>
              );
            })}
          </div>

          <div className="mt-10 rounded-2xl bg-white border border-emerald-100 p-6 flex flex-col sm:flex-row items-center gap-5">
            <div className="h-12 w-12 rounded-xl brand-gradient-bg flex items-center justify-center shrink-0">
              <Sparkles className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <div className="font-display font-bold text-zinc-900">
                Nicht sicher, welche Leistung du brauchst?
              </div>
              <p className="text-sm text-zinc-600 mt-1">
                Beschreib uns kurz deinen Schaden — wir empfehlen dir die passende Gutachten-Art unverbindlich und kostenlos.
              </p>
            </div>
            <a href="#kontakt">
              <Button className="brand-gradient-bg text-white">
                Jetzt fragen <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Service Area + Map */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid lg:grid-cols-2 gap-10 items-start" data-testid="partner-service-area">
        <div>
          <h2 className="font-display text-3xl font-bold tracking-tight">Standort & Einzugsgebiet</h2>
          <p className="text-zinc-600 mt-2">
            {partner.name} ist für dich vor Ort verfügbar — bis zu <strong>{partner.service_radius_km} km</strong> rund um {partner.city || "den Standort"}.
          </p>
          <div className="mt-5 rounded-2xl border border-zinc-200 bg-white p-5" data-testid="partner-contact-card">
            {/* 2-Spalten-Layout: links Standort+Telefon+Handy, rechts E-Mail+Website+Sprachen */}
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {/* Linke Spalte */}
              <div className="space-y-3">
                <Row icon={MapPin} label="Standort" value={`${partner.postal_code} ${partner.city}, ${partner.country}`} />
                {phone && (
                  <Row
                    icon={Phone}
                    label="Telefon"
                    value={<a href={`tel:${phone.replace(/\s/g, "")}`} className="text-emerald-700 hover:underline" data-testid="contact-card-phone">{phone}</a>}
                  />
                )}
                {mobile && (
                  <Row
                    icon={Smartphone}
                    label="Handy"
                    value={<a href={`tel:${mobile.replace(/\s/g, "")}`} className="text-emerald-700 hover:underline" data-testid="contact-card-mobile">{mobile}</a>}
                  />
                )}
              </div>
              {/* Rechte Spalte */}
              <div className="space-y-3">
                <Row
                  icon={Mail}
                  label="E-Mail"
                  value={<a href={`mailto:${brandedEmail}`} className="text-emerald-700 hover:underline break-all" data-testid="contact-card-email">{brandedEmail}</a>}
                />
                <Row
                  icon={Globe}
                  label="Website"
                  value={
                    <a
                      href={`https://${brandedWeb}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-emerald-700 hover:underline break-all"
                      data-testid="contact-card-web"
                    >
                      {brandedWeb}
                    </a>
                  }
                />
                {(partner.languages || []).length > 0 && (
                  <Row icon={Languages} label="Sprachen" value={partner.languages.join(" · ")} />
                )}
              </div>
            </div>

            {/* Versorgte PLZ als Vollbreite-Zeile unten */}
            {(partner.service_postal_codes || []).length > 0 && (
              <div className="mt-4 pt-4 border-t border-zinc-100">
                <Row
                  icon={MapPin}
                  label="Versorgte PLZ"
                  value={
                    <div className="flex flex-wrap gap-1">
                      {partner.service_postal_codes.map((plz) => (
                        <span key={plz} className="text-[11px] font-mono bg-zinc-100 text-zinc-700 px-1.5 py-0.5 rounded">{plz}</span>
                      ))}
                    </div>
                  }
                />
              </div>
            )}
          </div>
        </div>
        <div className="rounded-2xl overflow-hidden border border-zinc-200 bg-zinc-50 h-[440px]">
          <iframe
            title={`Karte ${partner.name}`}
            src={
              partner.geo_lat && partner.geo_lng
                ? `https://www.google.com/maps?q=${partner.geo_lat},${partner.geo_lng}&z=12&output=embed`
                : `https://www.google.com/maps?q=${encodeURIComponent(`${partner.postal_code} ${partner.city}`)}&output=embed`
            }
            className="w-full h-full"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </section>

      {/* "Was tun nach einem Unfall?" */}
      <section id="unfall-leitfaden" className="bg-zinc-900 text-white py-16 scroll-mt-20" data-testid="partner-accident-guide">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <Badge className="bg-white/10 text-white border border-white/20">
            <AlertTriangle className="h-3 w-3 mr-1" /> Sofort-Leitfaden
          </Badge>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-4">
            Was tun nach einem Unfall?
          </h2>
          <p className="text-zinc-300 mt-3 max-w-3xl">
            Die ersten 30 Minuten entscheiden über Beweislage, Versicherungsleistung und reibungslose Schadenregulierung. Folge diesen 7 Schritten:
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-9">
            {ACCIDENT_STEPS.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.num} className="rounded-2xl bg-white/5 border border-white/10 p-5 hover:bg-white/10 transition" data-testid={`accident-step-${s.num}`}>
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center">
                      <Icon className="h-5 w-5 text-white" />
                    </div>
                    <div className="font-mono text-xs text-emerald-400">SCHRITT {s.num}</div>
                  </div>
                  <div className="font-display font-bold text-lg mt-3">{s.title}</div>
                  <p className="text-sm text-zinc-300 leading-relaxed mt-1.5">{s.text}</p>
                </div>
              );
            })}
          </div>
          <div className="rounded-2xl bg-gradient-to-r from-cyan-500/15 to-emerald-500/15 border border-emerald-400/30 p-6 mt-10 flex flex-col sm:flex-row items-center gap-5">
            <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-cyan-400 to-emerald-400 flex items-center justify-center shrink-0">
              <Sparkles className="h-7 w-7 text-zinc-900" />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <div className="font-display font-bold text-lg">Profi-Tipp: Beauftrage <span className="text-emerald-300">{partner.name}</span> als unabhängigen Sachverständigen.</div>
              <p className="text-sm text-zinc-300 mt-1">Bei einem Haftpflichtschaden trägt die gegnerische Versicherung die Kosten — du behältst die volle Kontrolle über das Verfahren.</p>
            </div>
            <a href="#kontakt"><Button size="lg" className="brand-gradient-bg text-white">Anfrage senden <ArrowRight className="h-4 w-4 ml-2" /></Button></a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-4xl mx-auto px-6 lg:px-10 py-16 scroll-mt-20" data-testid="partner-faq">
        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Häufige Fragen</h2>
        <p className="text-zinc-600 mt-2">Antworten auf die wichtigsten Fragen rund um Kfz-Gutachten.</p>
        <div className="mt-8 divide-y divide-zinc-100 rounded-2xl border border-zinc-200 bg-white overflow-hidden">
          {FAQS.map((f, i) => <FAQItem key={i} q={f.q} a={f.a} idx={i} />)}
        </div>
      </section>

      {/* Contact form */}
      <section id="kontakt" className="bg-zinc-50/70 py-16 border-t border-zinc-200 scroll-mt-20" data-testid="partner-contact">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2">
            <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">
              <MessageSquare className="h-3 w-3 mr-1" /> Anfrage senden
            </Badge>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-4">
              Schnell, unverbindlich & kostenlos
            </h2>
            <p className="text-zinc-600 mt-3 leading-relaxed">
              Beschreib uns kurz deinen Schaden — {partner.name.split(" ")[0]} meldet sich innerhalb von 4 Stunden persönlich bei dir.
            </p>
            <div className="space-y-3 mt-6 text-sm">
              {phone && <div className="flex items-center gap-3"><Phone className="h-4 w-4 text-emerald-600" /> <a href={`tel:${phone.replace(/\s/g, "")}`} className="hover:underline">{phone}</a></div>}
              {mobile && <div className="flex items-center gap-3"><Smartphone className="h-4 w-4 text-emerald-600" /> <a href={`tel:${mobile.replace(/\s/g, "")}`} className="hover:underline">{mobile}</a></div>}
              <div className="flex items-center gap-3"><Mail className="h-4 w-4 text-emerald-600" /> <a href={`mailto:${brandedEmail}`} className="hover:underline break-all">{brandedEmail}</a></div>
              <div className="flex items-center gap-3"><Globe className="h-4 w-4 text-emerald-600" /> <a href={`https://${brandedWeb}`} target="_blank" rel="noopener noreferrer" className="hover:underline break-all">{brandedWeb}</a></div>
              <div className="flex items-center gap-3"><MapPin className="h-4 w-4 text-emerald-600" /> {partner.postal_code} {partner.city}</div>
            </div>
          </div>
          <Card className="lg:col-span-3 p-6 sm:p-8">
            <form onSubmit={onSubmit} className="space-y-4" data-testid="partner-contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Dein Name" required>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Max Mustermann" data-testid="contact-name" />
                </Field>
                <Field label="E-Mail" required>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="max@beispiel.de" data-testid="contact-email" />
                </Field>
                <Field label="Telefon (optional)">
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+49 …" data-testid="contact-phone" />
                </Field>
                <Field label="PLZ (optional)">
                  <Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} placeholder="z. B. 10115" data-testid="contact-plz" />
                </Field>
              </div>
              <Field label="Art des Schadens">
                <div className="flex flex-wrap gap-2 mt-1.5">
                  {[
                    { v: "haftpflicht", l: "Haftpflicht" },
                    { v: "kasko", l: "Kasko" },
                    { v: "wertgutachten", l: "Wertgutachten" },
                    { v: "other", l: "Sonstiges" },
                  ].map((o) => (
                    <button
                      key={o.v}
                      type="button"
                      onClick={() => setForm({ ...form, case_type: o.v })}
                      className={cn("px-4 py-1.5 rounded-full text-sm font-semibold border transition",
                        form.case_type === o.v
                          ? "bg-emerald-600 text-white border-emerald-600"
                          : "bg-white text-zinc-700 border-zinc-200 hover:border-emerald-300")}
                      data-testid={`contact-type-${o.v}`}
                    >
                      {o.l}
                    </button>
                  ))}
                </div>
              </Field>
              <Field label="Deine Nachricht" required>
                <Textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Beschreib kurz, wann der Unfall war, wo das Fahrzeug steht und welche Schäden zu sehen sind."
                  rows={5}
                  data-testid="contact-message"
                />
              </Field>
              <Button type="submit" size="lg" className="brand-gradient-bg text-white w-full" disabled={submitting} data-testid="contact-submit">
                {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-2" />}
                {submitting ? "Wird gesendet…" : "Anfrage senden"}
              </Button>
              <p className="text-[11px] text-zinc-400 text-center">
                Mit dem Absenden stimmst du der Übermittlung deiner Daten an {partner.name} zu. Keine Weitergabe an Dritte.
              </p>
            </form>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-200 py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-zinc-500">
          <div className="flex items-center gap-3">
            <img src="/easy-gutachter-logo.png" alt="" className="h-8 w-auto opacity-80" />
            <span>© {new Date().getFullYear()} Easy Gutachter · Alle Rechte vorbehalten</span>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/impressum" className="hover:text-zinc-900">Impressum</Link>
            <Link to="/datenschutz" className="hover:text-zinc-900">Datenschutz</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function TrustItem({ icon: Icon, title, desc }) {
  return (
    <div className="flex flex-col items-center gap-1.5">
      <div className="h-9 w-9 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center">
        <Icon className="h-4 w-4" strokeWidth={1.8} />
      </div>
      <div className="font-display font-bold text-zinc-900 text-sm">{title}</div>
      <div className="text-zinc-500 text-[11px]">{desc}</div>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3 text-sm">
      <Icon className="h-4 w-4 text-zinc-400 mt-0.5 shrink-0" />
      <div className="flex-1">
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-zinc-500">{label}</div>
        <div className="text-zinc-900 mt-0.5">{value}</div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500">
        {label}{required && <span className="text-rose-500 ml-1">*</span>}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function FAQItem({ q, a, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div data-testid={`faq-item-${idx}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full text-left flex items-start justify-between gap-4 px-5 py-4 hover:bg-zinc-50/70 transition"
      >
        <span className="font-display font-semibold text-zinc-900">{q}</span>
        <ChevronRight className={cn("h-4 w-4 text-zinc-400 mt-1 transition-transform shrink-0", open && "rotate-90")} />
      </button>
      {open && <div className="px-5 pb-5 text-sm text-zinc-600 leading-relaxed">{a}</div>}
    </div>
  );
}

// SEO helpers
function setMeta(name, content) {
  let m = document.querySelector(`meta[name="${name}"]`);
  if (!m) { m = document.createElement("meta"); m.setAttribute("name", name); document.head.appendChild(m); }
  m.setAttribute("content", content);
}
function setOg(prop, content) {
  let m = document.querySelector(`meta[property="${prop}"]`);
  if (!m) { m = document.createElement("meta"); m.setAttribute("property", prop); document.head.appendChild(m); }
  m.setAttribute("content", content);
}
